import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { Select } from "@/components/ui/select";
import { UserRow, roleDisplayLabel, type InviteListInfo } from "@/features/admin/users-table";
import { isLabUser } from "@/features/admin/users-lab";
import { fetchAdminUsers, fetchAdminInvites, fetchAdminGroups, fetchAdminRoles, type AdminUser } from "@/lib/api";
import { Pagination } from "@/components/ui/pagination";
import { PermissionGate } from "@/components/permission/permission-gate";
import { cn } from "@/lib/utils";
import type { AppUser, UserRole } from "@/types";
import { usePlainErrorMessage } from "@/components/data-display/api-failure";
import { classifyFailure, legacyErrorReason } from "@/lib/api-failure";
import {
  hasPermission,
  roleDisplayLabel as permissionRoleLabel,
  type AppRole,
} from "@/lib/permissions";

const STATUS_OPTIONS = ["Active", "Invited", "Pending", "Expired", "Accepted", "Suspended"] as const;

/** Rows per page. The directory is small today but unbounded in principle —
 *  an organization can hold thousands of principals and invitations — so the
 *  table shows a page at a time and states the window it is showing. */
const PAGE_SIZE = 10;

function toAppUser(u: AdminUser): AppUser {
  return {
    id: u.principalId,
    name: u.displayName || u.email,
    email: u.email,
    role: (u.roleName || u.principalType || "Organization Admin") as UserRole,
    groups: u.groupNames ? u.groupNames.split(",") : [],
    mfa: u.mfaMethod || "",
    status: u.status === "ACTIVE" ? "Active" : u.status === "DEACTIVATED" ? "Suspended" : u.status === "EXPIRED" ? "Suspended" : u.status === "INVITED" ? "Invited" : "Active",
    lastActive: u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—",
  };
}

/** The invite lifecycle as the API reports it → what the administrator reads.
 * PENDING and ISSUED both mean "sent, awaiting acceptance"; an unrecognised
 * status is shown verbatim rather than guessed into one of the three. */
function inviteStatusLabel(status: string): string {
  if (status === "PENDING" || status === "ISSUED") return "Pending";
  if (status === "EXPIRED") return "Expired";
  if (status === "ACCEPTED") return "Accepted";
  return status;
}

/** What the row filters and the stat tiles compare against: an invitation's own
 * lifecycle label, or the user's status for a real person. */
function rowStatus(user: AppUser, invite?: InviteListInfo): string {
  return invite?.statusLabel || user.status;
}

/**
 * The roles that grant the `user:read` permission (ADM-06), named in a denial.
 *
 * Derived from the permission table in `lib/permissions.ts` — the module's own
 * header calls it the single source of truth for RBAC — so a role gaining or
 * losing `user:read` moves this copy with it. The generic organization admin is
 * excluded by construction: the sentence's second half already names it, and a
 * viewer reading a denial by definition does not hold it. Display labels come
 * from `roleDisplayLabel`, so the reader sees "Wallet Administrator", never the
 * raw `wallet-admin` id a role assignment screen would use.
 */
const USERS_READ_ROLE_KEYS: AppRole[] = [
  "security-admin",
  "developer",
  "wallet-admin",
  "wallet_operator",
  "compliance-officer",
  "policy_approver",
  "treasury-operator",
  "finance_operator",
  "finance_viewer",
  "auditor",
  "support-readonly",
];

function usersReadRoleNames(): string {
  const labels = USERS_READ_ROLE_KEYS.filter((role) => hasPermission(role, "user:read")).map((role) =>
    permissionRoleLabel(role),
  );
  if (labels.length === 0) return "Organization Admin";
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
}

function UserStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-[10px] border border-line bg-surface px-[18px] py-[16px]">
      <div className="text-[24px] font-bold leading-[1.1] tracking-tight text-ink">{value}</div>
      <div className="mt-0.5 text-[12px] font-medium text-muted">{label}</div>
    </div>
  );
}

export default function AdminUsersPage() {
  const plainError = usePlainErrorMessage();
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const [group, setGroup] = useState("all");
  const [mfa, setMfa] = useState("all");
  const [status, setStatus] = useState("all");
  const [users, setUsers] = useState<AppUser[]>([]);
  const [inviteDelivery, setInviteDelivery] = useState<Record<string, string>>({});
  const [inviteInfo, setInviteInfo] = useState<Record<string, InviteListInfo>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupOptions, setGroupOptions] = useState<string[]>([]);
  const [roleLabels, setRoleLabels] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  // UX audit D06: lab tooling creates an invitee on every flow run, so the dev
  // e2e-admin persona sees hundreds of test users with only a handful active.
  // Those rows are hidden by default and revealed by the footer toggle, exactly
  // like the wallets list's "Show test and lab wallets" (ALL-5795).
  const [showLabUsers, setShowLabUsers] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchAdminUsers(), fetchAdminInvites()])
      .then(([usersResult, invitesResult]) => {
        if (cancelled) return;
        if (usersResult.error && invitesResult.error) {
          setError(legacyErrorReason(usersResult.error));
          setLoading(false);
          return;
        }
        const apiUsers: AppUser[] = !usersResult.error
          ? usersResult.users.map(toAppUser)
          : [];
        // Every invite the API returns is listed — an expired or accepted invite
        // used to be filtered out here, which hid the fact that it had lapsed.
        const invites = !invitesResult.error && invitesResult.invites ? invitesResult.invites : [];
        const invitedUsers: AppUser[] = invites.map(inv => ({
          id: inv.inviteId,
          name: inv.displayName || inv.email,
          email: inv.email,
          role: roleDisplayLabel(inv.roleName || "Organization Admin") as UserRole,
          groups: [],
          mfa: "",
          // The domain status stays "Invited" (the row is an invitation, not a
          // user); the real lifecycle — Pending/Expired/Accepted — rides in the
          // side map below and is what the badge and the filters read.
          status: "Invited" as const,
          lastActive: inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : "—",
        }));
        // Delivery status and invite lifecycle are invite-only, so they're kept
        // in side maps keyed by invite id rather than widening AppUser (shared
        // with real users).
        const inviteDelivery: Record<string, string> = Object.fromEntries(
          invites.filter(inv => inv.deliveryStatus).map(inv => [inv.inviteId, inv.deliveryStatus]),
        );
        const inviteInfo: Record<string, InviteListInfo> = Object.fromEntries(
          invites.map(inv => [
            inv.inviteId,
            { statusLabel: inviteStatusLabel(inv.status), expiresAt: inv.expiresAt },
          ]),
        );
        setUsers([...apiUsers, ...invitedUsers]);
        setInviteDelivery(inviteDelivery);
        setInviteInfo(inviteInfo);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("NETWORK_ERROR");
        setLoading(false);
      });
    fetchAdminGroups().then((groupsResult) => {
      if (cancelled) return;
      if (!groupsResult.error && groupsResult.groups.length > 0) {
        setGroupOptions(groupsResult.groups.map((g) => g.name));
      }
    });
    // The role catalog names the roles the rows hold. A gateway user record
    // carries only `roleName` (`organization_admin`), which is an identifier,
    // not a job title — the catalog is what turns it into the display name
    // the role assignment screens already use. A failure here is not a page
    // failure: the column falls back to a title-cased roleName.
    fetchAdminRoles().then((rolesResult) => {
      if (cancelled) return;
      if (rolesResult.error || rolesResult.roles.length === 0) return;
      setRoleLabels(
        Object.fromEntries(
          rolesResult.roles.map((r) => [r.roleName, r.displayName || roleDisplayLabel(r.roleName)]),
        ),
      );
    });
    return () => { cancelled = true; };
  }, []);

  // Split the loaded directory into the rows a person was actually invited
  // against and the test/lab rest. `users` itself is never mutated: the rows
  // are only hidden, and the toggle brings every one of them back.
  const { namedUsers, labUserCount } = useMemo(() => {
    const named: AppUser[] = [];
    let lab = 0;
    for (const u of users) {
      if (isLabUser(u)) lab += 1;
      else named.push(u);
    }
    return { namedUsers: named, labUserCount: lab };
  }, [users]);

  // The stat tiles, the filters, the table and its pagination all describe the
  // visible set — the invited users, or every user once "Show test and lab
  // users" is expanded.
  const listedUsers = showLabUsers ? users : namedUsers;

  const roleOptions = useMemo(
    () => [...new Set(listedUsers.map((u) => u.role))].sort(),
    [listedUsers],
  );

  const mfaOptions = useMemo(
    () => [...new Set(listedUsers.map((u) => u.mfa).filter(Boolean))].sort(),
    [listedUsers],
  );

  const stats = useMemo(
    () => ({
      total: listedUsers.length,
      active: listedUsers.filter((u) => u.status === "Active").length,
      // "Pending" covers a person who has been invited but has not accepted,
      // and an invitation still awaiting acceptance. An expired or accepted
      // invitation is neither, so it is not counted here.
      pending: listedUsers.filter((u) => {
        const info = inviteInfo[u.id];
        return info ? rowStatus(u, info) === "Pending" : u.status === "Invited";
      }).length,
      suspended: listedUsers.filter((u) => u.status === "Suspended").length,
    }),
    [listedUsers, inviteInfo]
  );

  const rows = useMemo(
    () =>
      listedUsers.filter((u) => {
        const q = query.toLowerCase();
        return (
          (!q || `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q)) &&
          (role === "all" || u.role === role) &&
          (group === "all" || u.groups.includes(group)) &&
          (mfa === "all" || u.mfa === mfa) &&
          (status === "all" || rowStatus(u, inviteInfo[u.id]) === status)
        );
      }),
    [listedUsers, inviteInfo, query, role, group, mfa, status]
  );

  // A filter change re-reads the directory from the first page: staying on
  // page 4 of the previous result would show an empty table whenever the
  // narrowed list is shorter than that. Toggling the lab rows changes the set
  // the pagination describes, so it resets to the first page too.
  useEffect(() => { setPage(1); }, [query, role, group, mfa, status, showLabUsers]);

  /** Reset every directory filter at once — the "Clear filters" action in the
   *  over-filtered empty state. The page returns to page 1 through the effect
   *  above, which is keyed on these same five values. */
  const clearFilters = () => {
    setQuery("");
    setRole("all");
    setGroup("all");
    setMfa("all");
    setStatus("all");
  };

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div>
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <a href="#" onClick={(e) => e.preventDefault()} className="text-primary no-underline hover:underline">
          Identity
        </a>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">Users</span>
      </nav>

      {/* The counters are counts *of the loaded list*, so a failed load leaves
          them at zero — four confident "0"s that read as "this organization
          has no users". With no list behind them they are not shown at all;
          the error below says what actually happened. */}
      {!error && (
        <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
          <UserStat value={String(stats.total)} label="Total Users" />
          <UserStat value={String(stats.active)} label="Active" />
          <UserStat value={String(stats.pending)} label="Pending" />
          <UserStat value={String(stats.suspended)} label="Suspended" />
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative w-[280px] max-w-full">
          <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, or role..."
            className="h-[36px] w-full rounded-[8px] border border-line bg-surface pl-9 pr-3 text-[13px] text-ink outline-none transition-colors placeholder:text-subtle focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
          />
        </div>
        <Select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          aria-label="Filter by role"
          className="rounded-[8px] py-[9px] pl-3 pr-7 text-[12px] text-muted"
        >
          <option value="all">All Roles</option>
          {/* The option's value stays the raw role the rows carry; only the
              text a person reads comes from the catalog. */}
          {roleOptions.map((r) => (
            <option key={r} value={r}>{roleLabels[r] ?? roleDisplayLabel(r)}</option>
          ))}
        </Select>
        <Select
          value={group}
          onChange={(e) => setGroup(e.target.value)}
          aria-label="Filter by group"
          className="rounded-[8px] py-[9px] pl-3 pr-7 text-[12px] text-muted"
        >
          <option value="all">All Groups</option>
          {groupOptions.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </Select>
        <Select
          value={mfa}
          onChange={(e) => setMfa(e.target.value)}
          aria-label="Filter by MFA method"
          className="rounded-[8px] py-[9px] pl-3 pr-7 text-[12px] text-muted"
        >
          <option value="all">All MFA</option>
          {mfaOptions.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </Select>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Filter by status"
          className="rounded-[8px] py-[9px] pl-3 pr-7 text-[12px] text-muted"
        >
          <option value="all">All Status</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Select>
        <PermissionGate permission="user:invite">
          <Link to="/admin/invite" className={cn(buttonVariants(), "ml-auto rounded-[8px]")}>
            + Invite User
          </Link>
        </PermissionGate>
      </div>

      {error && !loading && (
        <div
          role="alert"
          data-testid="users-load-error"
          className="mb-4 rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger"
        >
          {/* ADM-06: a 403 used to read the same as a 500 ("Couldn't load
              users"), so an approver without users-read access was told the
              page had failed rather than that they need a role. A denial names
              the roles that grant `user:read` instead, the way
              features/wallets/wallet-request.tsx does. Everything else keeps
              the plain sentence: the gateway's code (`HTTP_500`) is a machine
              handle an administrator cannot act on, and the users endpoints do
              not mint a support reference for this response. */}
          {classifyFailure({ reasonCode: error }) === "denied" ? (
            <>Viewing users needs the {usersReadRoleNames()} role. Ask an organization admin to assign it.</>
          ) : (
            <>Couldn&apos;t load users. {plainError({ reasonCode: error })}</>
          )}
        </div>
      )}

      {loading && (
        <div className="mb-4 text-center text-sm text-muted">Loading users...</div>
      )}

      <TableWrapper>
        <Table>
          <thead>
            <tr className="border-b border-line bg-bg">
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Name</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Email</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Role</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Groups</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">MFA</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Status</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Delivery</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Last Active</th>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                deliveryStatus={inviteDelivery[u.id]}
                invite={inviteInfo[u.id]}
                roleLabels={roleLabels}
              />
            ))}
          </tbody>
        </Table>
        {rows.length > 0 && !loading && !error && (
          <Pagination
            page={currentPage}
            pageCount={pageCount}
            onPageChange={setPage}
            totalItems={rows.length}
            pageSize={PAGE_SIZE}
          />
        )}
        {rows.length === 0 && !loading && !error && (
          users.length === 0 ? (
            <div className="px-4 py-10 text-center" data-testid="users-empty" data-empty-kind="no-users">
              <p className="text-sm font-semibold text-ink">No users yet</p>
              <p className="mx-auto mt-1 max-w-[420px] text-sm text-muted">
                Invite a team member to give them access. Every invitation you send appears
                here with its status until it is accepted or expires.
              </p>
              <PermissionGate permission="user:invite">
                <Link
                  to="/admin/invite"
                  className={cn(buttonVariants({ variant: "secondary", size: "sm" }), "mt-4")}
                >
                  Invite a user
                </Link>
              </PermissionGate>
            </div>
          ) : (
            <div className="px-4 py-10 text-center" data-testid="users-empty" data-empty-kind="filtered">
              <p className="text-sm font-semibold text-ink">No users match these filters</p>
              <p className="mx-auto mt-1 max-w-[420px] text-sm text-muted">
                Clear the filters to see everyone in this organization.
              </p>
              <button
                type="button"
                data-testid="users-clear-filters"
                onClick={clearFilters}
                className={cn(buttonVariants({ variant: "secondary", size: "sm" }), "mt-4")}
              >
                Clear filters
              </button>
            </div>
          )
        )}
        {/* UX audit D06: the collapsed footer toggle, shown whenever there is
            something to reveal — including when every row is a lab user and the
            visible set is empty. A real button, so it stays keyboard operable. */}
        {labUserCount > 0 && (
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line px-4 py-2.5"
            data-testid="admin-users-lab-toggle"
          >
            <button
              type="button"
              onClick={() => setShowLabUsers((shown) => !shown)}
              aria-expanded={showLabUsers}
              className="inline-flex items-center gap-1.5 rounded-md text-xs font-medium text-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
            >
              {showLabUsers ? "Hide" : "Show"} test and lab users ({labUserCount})
            </button>
            {!showLabUsers && (
              <span className="text-xs text-subtle" data-testid="admin-users-lab-hidden-count">
                {labUserCount} test and lab user{labUserCount === 1 ? "" : "s"} hidden
              </span>
            )}
          </div>
        )}
      </TableWrapper>
    </div>
  );
}
